import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  clearGamificationPin,
  getMyGamification,
  getPublicGamification,
  markGamificationEventsSeen,
  setGamificationPin,
} from '@/api/gamification';
import { useAuth } from '@/hooks/auth';
import { useGamificationFeatureEnabled } from '@/hooks/data/config';
import type {
  GamificationMeState,
  GamificationPublicState,
  SetGamificationPinResponse,
  TrophyId,
} from '@/types/gamification';
import {
  applyOptimisticSeenToMyGamification,
  applyPinnedToMyGamification,
  applyPinnedToPublicGamification,
  createSeenSnapshot,
  restoreSeenSnapshot,
} from './gamification-cache.helpers';

export const gamificationQueryKeys = {
  all: () => ['gamification'] as const,
  me: () => [...gamificationQueryKeys.all(), 'me'] as const,
  profile: (userId: string) => [...gamificationQueryKeys.all(), 'profile', userId] as const,
};

type PinMutationContext = {
  previousMe: GamificationMeState | undefined;
  previousOwnProfile: GamificationPublicState | undefined;
};

function useAuthHydrated(): boolean {
  const [isHydrated, setIsHydrated] = useState<boolean>(() =>
    typeof window !== 'undefined' && useAuth.persist.hasHydrated(),
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (useAuth.persist.hasHydrated()) {
      setIsHydrated(true);
      return;
    }

    const unsubscribe = useAuth.persist.onFinishHydration(() => {
      setIsHydrated(true);
    });

    return unsubscribe;
  }, []);

  return isHydrated;
}

async function invalidateGamificationRelations(
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['gamification'] }),
    queryClient.invalidateQueries({ queryKey: ['profiles'] }),
    queryClient.invalidateQueries({ queryKey: ['duels'] }),
    queryClient.invalidateQueries({ queryKey: ['duelVotes'] }),
    queryClient.invalidateQueries({ queryKey: ['captain'] }),
  ]);
}

export function useMyGamification() {
  const isAuthenticated = useAuth(state => state.isAuthenticated);
  const ownUserId = useAuth(state => state.user?.id ?? null);
  const isAuthHydrated = useAuthHydrated();
  const gamificationEnabled = useGamificationFeatureEnabled();

  return useQuery<GamificationMeState>({
    queryKey: gamificationQueryKeys.me(),
    queryFn: getMyGamification,
    enabled: gamificationEnabled && isAuthHydrated && isAuthenticated && !!ownUserId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function usePublicGamification(userId: string | null | undefined) {
  const normalizedUserId = userId?.trim() ?? '';
  const gamificationEnabled = useGamificationFeatureEnabled();

  return useQuery<GamificationPublicState>({
    queryKey: gamificationQueryKeys.profile(normalizedUserId),
    queryFn: () => getPublicGamification(normalizedUserId),
    enabled: gamificationEnabled && normalizedUserId.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSetGamificationPin() {
  const queryClient = useQueryClient();
  const ownUserId = useAuth(state => state.user?.id ?? null);

  return useMutation<SetGamificationPinResponse, unknown, { trophyId: TrophyId }, PinMutationContext>({
    mutationFn: ({ trophyId }) => setGamificationPin(trophyId),
    onMutate: async ({ trophyId }) => {
      await queryClient.cancelQueries({ queryKey: gamificationQueryKeys.all() });

      const previousMe = queryClient.getQueryData<GamificationMeState>(gamificationQueryKeys.me());
      const ownProfileKey = ownUserId ? gamificationQueryKeys.profile(ownUserId) : null;
      const previousOwnProfile = ownProfileKey
        ? queryClient.getQueryData<GamificationPublicState>(ownProfileKey)
        : undefined;

      queryClient.setQueryData<GamificationMeState>(gamificationQueryKeys.me(), oldValue => {
        if (!oldValue) {
          return oldValue;
        }

        return applyPinnedToMyGamification(oldValue, {
          trophy_id: trophyId,
          source: 'manual',
        });
      });

      if (ownProfileKey) {
        queryClient.setQueryData<GamificationPublicState>(ownProfileKey, oldValue => {
          if (!oldValue) {
            return oldValue;
          }

          return applyPinnedToPublicGamification(oldValue, trophyId);
        });
      }

      return { previousMe, previousOwnProfile };
    },
    onSuccess: async pin => {
      const pinnedTrophyId = pin?.trophy_id ?? null;

      queryClient.setQueryData<GamificationMeState>(gamificationQueryKeys.me(), oldValue => {
        if (!oldValue) {
          return oldValue;
        }
        return applyPinnedToMyGamification(oldValue, pin);
      });

      if (ownUserId) {
        queryClient.setQueryData<GamificationPublicState>(gamificationQueryKeys.profile(ownUserId), oldValue => {
          if (!oldValue) {
            return oldValue;
          }

          return applyPinnedToPublicGamification(oldValue, pinnedTrophyId);
        });
      }

      await invalidateGamificationRelations(queryClient);
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      queryClient.setQueryData(gamificationQueryKeys.me(), context.previousMe);
      if (ownUserId) {
        queryClient.setQueryData(gamificationQueryKeys.profile(ownUserId), context.previousOwnProfile);
      }
    },
  });
}

export function useClearGamificationPin() {
  const queryClient = useQueryClient();
  const ownUserId = useAuth(state => state.user?.id ?? null);

  return useMutation<SetGamificationPinResponse, unknown, void>({
    mutationFn: clearGamificationPin,
    onMutate: async () => {
      // Do not optimistically null the pin: auto-pin is server-only and null would
      // briefly hide the effective badge (or tempt a client-side fallback).
      await queryClient.cancelQueries({ queryKey: gamificationQueryKeys.all() });
    },
    onSuccess: async pin => {
      const pinnedTrophyId = pin?.trophy_id ?? null;

      queryClient.setQueryData<GamificationMeState>(gamificationQueryKeys.me(), oldValue => {
        if (!oldValue) {
          return oldValue;
        }
        return applyPinnedToMyGamification(oldValue, pin);
      });

      if (ownUserId) {
        queryClient.setQueryData<GamificationPublicState>(gamificationQueryKeys.profile(ownUserId), oldValue => {
          if (!oldValue) {
            return oldValue;
          }

          return applyPinnedToPublicGamification(oldValue, pinnedTrophyId);
        });
      }

      await invalidateGamificationRelations(queryClient);
    },
  });
}

type MarkEventsSeenVariables = {
  latestEventId: string | null;
};

type MarkEventsSeenContext = {
  shouldRollback: boolean;
  snapshot: ReturnType<typeof createSeenSnapshot>;
};

export function useMarkGamificationEventsSeen() {
  const queryClient = useQueryClient();
  const lastConfirmedEventIdRef = useRef<string | null>(null);

  return useMutation<null, unknown, MarkEventsSeenVariables, MarkEventsSeenContext>({
    mutationFn: async ({ latestEventId }) => {
      if (!latestEventId) {
        return null;
      }
      if (lastConfirmedEventIdRef.current === latestEventId) {
        return null;
      }

      await markGamificationEventsSeen(latestEventId);
      return null;
    },
    onMutate: async ({ latestEventId }) => {
      if (!latestEventId) {
        return { shouldRollback: false, snapshot: undefined };
      }

      await queryClient.cancelQueries({ queryKey: gamificationQueryKeys.me() });
      const current = queryClient.getQueryData<GamificationMeState>(gamificationQueryKeys.me());

      if (!current) {
        return { shouldRollback: false, snapshot: undefined };
      }

      const snapshot = createSeenSnapshot(current);
      const optimistic = applyOptimisticSeenToMyGamification(current);
      queryClient.setQueryData(gamificationQueryKeys.me(), optimistic);

      return {
        shouldRollback: true,
        snapshot,
      };
    },
    onSuccess: (_response, { latestEventId }) => {
      if (latestEventId) {
        lastConfirmedEventIdRef.current = latestEventId;
      }
    },
    onError: (_error, _variables, context) => {
      const snapshot = context?.snapshot;
      if (!context?.shouldRollback || !snapshot) {
        return;
      }

      queryClient.setQueryData<GamificationMeState>(gamificationQueryKeys.me(), current => {
        if (!current) {
          return current;
        }
        return restoreSeenSnapshot(current, snapshot);
      });
    },
  });
}
